import { MutableRefObject } from "react";
import { Spinner } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Button from "components/base/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { useCriarKit, useAlterarKit } from "hooks/useKits";
import { SalvarKitDto } from "services/kitService";
import { KitFormValues } from "pages/retaguarda/kitForm";
import { useToast } from "providers/ToastProvider";

interface BotaoSalvarKitProps {
  id?: string;
  bypassBlockerRef?: MutableRefObject<boolean>;
}

const TOLERANCE = 0.01;

const BotaoSalvarKit = ({ id, bypassBlockerRef }: BotaoSalvarKitProps) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { handleSubmit, reset, setError } = useFormContext<KitFormValues>();

  const criarMutation = useCriarKit();
  const alterarMutation = useAlterarKit();

  // 400 (validação do backend, ex: componente controlado/PBM) traz a mensagem em
  // response.data.message. O getMutationErrorHandler dos hooks só trata 403 — aqui
  // mostramos o motivo da rejeição no toast pra não falhar silenciosamente.
  const onErroSalvar = (error: any) => {
    const msg =
      error?.response?.data?.message ??
      "Não foi possível salvar o kit. Verifique os componentes e tente novamente.";
    showToast(msg, "error");
  };

  const isPending = criarMutation.isPending || alterarMutation.isPending;

  const onSubmit = (values: KitFormValues) => {
    // Validação: soma precoNoKit deve bater com precoKit (tolerância 0.01)
    if (values.componentes.length > 0 && values.precoKit > 0) {
      const somaPrecoNoKit = values.componentes.reduce(
        (acc, c) => acc + (c.precoNoKit ?? 0),
        0
      );
      if (Math.abs(somaPrecoNoKit - values.precoKit) > TOLERANCE) {
        setError("precoKit", {
          type: "manual",
          message: `A soma dos preços no kit (R$ ${somaPrecoNoKit.toFixed(2)}) não confere com o Preço do Kit (R$ ${values.precoKit.toFixed(2)}). Ajuste os componentes ou use "Ratear Preço do Kit".`,
        });
        return;
      }
    }

    const dto: SalvarKitDto = {
      descricao: values.descricao,
      precoKit: values.precoKit,
      inativo: values.inativo,
      componentes: values.componentes.map((c) => ({
        componenteProdutoId: c.componenteProdutoId,
        quantidade: c.quantidade,
        precoNoKit: c.precoNoKit,
      })),
    };

    // Libera o blocker e limpa o dirty antes de navegar
    const irParaLista = () => {
      if (bypassBlockerRef) bypassBlockerRef.current = true;
      reset(values);
      navigate("/cadastros/kits");
    };

    if (id) {
      alterarMutation.mutate({ id, data: dto }, { onSuccess: irParaLista, onError: onErroSalvar });
    } else {
      criarMutation.mutate(dto, { onSuccess: irParaLista, onError: onErroSalvar });
    }
  };

  return (
    <Button
      variant="primary"
      onClick={handleSubmit(onSubmit)}
      disabled={isPending}
    >
      {isPending ? (
        <>
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
            className="me-2"
          />
          Salvando...
        </>
      ) : (
        <>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Salvar
        </>
      )}
    </Button>
  );
};

export default BotaoSalvarKit;
